import {isChunkLoadError} from "@/lib/deploymentRecoveryUtils"

/**
 * Loads the sh-live-capture module from the tenant's CDN, with retries.
 * Kept separate from MobileApp: a dynamic import of a runtime URL cannot be
 * intercepted by module mocking when written inline, so this is the test seam.
 */

const importModule = (specifier) => import(/* @vite-ignore */ specifier)

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms))

const TIMEOUT_ERROR_NAME = "SdkModuleLoadTimeout"

// A stalled fetch never rejects, so every attempt races a timeout
const withTimeout = (promise, ms) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => {
    const error = new Error(`SDK module load timed out after ${ms}ms`)
    error.name = TIMEOUT_ERROR_NAME
    reject(error)
  }, ms)
  promise.then(
    value => {
      clearTimeout(timer)
      resolve(value)
    },
    error => {
      clearTimeout(timer)
      reject(error)
    }
  )
})

// Only transient failures retry; a module-evaluation error would fail identically every attempt
const isRetryable = (error) => error?.name === TIMEOUT_ERROR_NAME || isChunkLoadError(error)

// Browsers replay a failed module fetch from the module map for an identical specifier,
// so retries use a unique token, monotonic for the page so a later load can't reuse a poisoned one
let retryToken = 0

// One shared load per page; a failed load is not memoised
let activeLoad = null

/** Test seam. */
export const resetIdentitySdkLoader = () => {
  activeLoad = null
}

export function loadIdentitySdkModule(cdnBaseUrl, {
  attempts = 3,
  baseDelayMs = 1000,
  attemptTimeoutMs = 10000,
  onRetry,
  importFn = importModule,
  delayFn = wait
} = {}) {
  if (!cdnBaseUrl) {
    return Promise.reject(new Error("No CDN url configured for this tenant"))
  }
  if (activeLoad) return activeLoad

  const cleanUrl = `${cdnBaseUrl}/sh-live-capture.js`

  // A timed-out attempt is abandoned, not cancelled, and can still win a later attempt's race;
  // racing the current import alongside the pool keeps a fast failure from being masked by a stalled one
  const pendingImports = []

  const attemptLoad = async (attempt) => {
    const specifier = attempt === 1 ? cleanUrl : `${cleanUrl}?shRetry=${++retryToken}`
    const current = importFn(specifier)
    pendingImports.push(current)
    try {
      return await withTimeout(Promise.race([Promise.any(pendingImports), current]), attemptTimeoutMs)
    } catch (error) {
      // AggregateError means every pending import failed; its last entry is the current attempt's
      const cause = error instanceof AggregateError
        ? error.errors[error.errors.length - 1]
        : error
      if (attempt >= attempts || !isRetryable(cause)) throw cause

      const nextRetryDelayMs = baseDelayMs * Math.pow(2, attempt - 1)
      // Reports the attempt about to run, matching the wrapper's convention
      onRetry?.({
        attempt: attempt + 1,
        maxAttempts: attempts,
        nextRetryDelayMs,
        lastError: cause?.message || "Unknown error"
      })
      await delayFn(nextRetryDelayMs)
      return attemptLoad(attempt + 1)
    }
  }

  activeLoad = attemptLoad(1).catch(error => {
    activeLoad = null
    throw error
  })
  return activeLoad
}
